import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { OSSIcon, OSSSearchBar, OSSButton } from "@oss/components";
import Pills from "./Pills.js";

const useStyles = makeStyles(theme => ({
  svgStyles: {
    height: "100%",
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 0
  },
  onTop: {
    zIndex: 1
  },
  pageHeader: {
    borderRadius: 3,
    marginBottom: theme.spacing(2),
    position: "relative",
    overflow: "hidden",
    //border: theme.border(),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      height: "66px",
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  mutlilinePageHeaderStyles: {
    [theme.breakpoints.up("sm")]: {
      height: "auto",
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1.5)
    },
    [theme.breakpoints.up("md")]: {
      height: "66px",
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  pageHeaderIcon: {
    marginRight: theme.spacing(1),
    color: "white",
    fontSize: "40px"
  },
  pageHeaderTitle: {
    color: "#fff",
    fontWeight: 700,
    whiteSpace: "nowrap",
    textAlign: "left",
    flex: 1
  },
  pageTitleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: theme.spacing(2),
    textAlign: "left",
    [theme.breakpoints.up("sm")]: {
      padding: [[theme.spacing(2), theme.spacing(4)]],
      width: "auto",
      borderTopRightRadius: "30px 50px",
      borderBottomRightRadius: "30px 50px",
      boxShadow: props =>
        props.color === "black"
          ? "0 0px 4px 4px rgba(200, 200, 200, 0.2)"
          : "0 0px 4px 4px rgba(100, 100, 100, 0.2)"
    }
  },
  multilinePageTitleContainer: {
    [theme.breakpoints.up("sm")]: {
      padding: [[theme.spacing(2), theme.spacing(1)]],
      width: "auto",
      borderTopRightRadius: "none",
      borderBottomRightRadius: "none",
      boxShadow: "none"
    },
    [theme.breakpoints.up("md")]: {
      padding: [[theme.spacing(2), theme.spacing(4)]],
      width: "auto",
      borderTopRightRadius: "30px 50px",
      borderBottomRightRadius: "30px 50px",
      boxShadow: props =>
        props.color === "black"
          ? "0 0px 4px 4px rgba(200, 200, 200, 0.2)"
          : "0 0px 4px 4px rgba(100, 100, 100, 0.2)"
    }
  },
  tertiaryButtonStyles: {
    margin: [[0, theme.spacing(1)]],
    backgroundColor: theme.palette.all.white[500],
    "&:hover": {
      backgroundColor: theme.palette.all.white[500]
    }
  },
  searchAndButtonWrapper: {
    display: "flex",
    alignItems: "center",
    padding: [[theme.spacing(1), theme.spacing(2)]],
    justifyContent: "space-between",
    [theme.breakpoints.up("sm")]: {
      padding: [[0, theme.spacing()]],
      width: "70%",
      justifyContent: "flex-end"
    }
  }
}));

/**
 * OSSCatalogHeader
 * @param {("black"|"orange"|"blue"|"green"|"turquoise")} color Maps to options in theme.palette.catalogColors
 * @param {array} children - Will add component to right end of the catalog header if used
 * @param {string} icon - Required for branding of each catalog
 * @param {string} title - Required text for branding of each catalog
 * @param {string} testid - defaults to catalog-header. Change if needed for testing
 * @param {bool} hasSearch - if provided, a search bar will show
 * @param {object} query - Required if hasSearch is true
 * @param {function} setQuery - Required if hasSearch is true
 * @param {function} buttonAction - Provide if button should be added to the header
 * @param {string} buttonLabel - Provide for button to have a label
 * @param {bool} multiline - use if the children passed to the component cause a wrapping issue (see Profiles header on tablet)
 */
const OSSCatalogHeader = ({
  color = "black",
  children,
  icon,
  iconProps = {},
  title,
  testid = "catalog-header",
  hasSearch = null,
  query,
  setQuery,
  searchLabel = "Search by name",
  buttonAction = null,
  buttonLabel = null,
  multiline = null
}) => {
  const classes = useStyles({ color });
  const mediumUp = useMediaQuery(theme => theme.breakpoints.up("md"));
  const smallUp = useMediaQuery(theme => theme.breakpoints.up("sm"));
  const theme = useTheme();
  const colors = theme.palette.catalogColors[color];

  const xsToSmViewBox = "10 35 83.58 50.72";
  const smToMdViewBox = "10 35 183.58 50.72";
  const mdUpViewBox = "10 35 983.58 100.72";

  const pillsViewBox = mediumUp
    ? mdUpViewBox
    : smallUp
    ? smToMdViewBox
    : xsToSmViewBox;

  return (
    <Grid
      container
      alignItems={smallUp ? "center" : "flex-start"}
      justifyContent="space-between"
      direction={smallUp ? "row" : "column"}
      className={[
        classes.pageHeader,
        multiline ? classes.mutlilinePageHeaderStyles : ""
      ].join(" ")}
      data-testid={`${color}-${testid}`}
      wrap="nowrap"
    >
      <span className={classes.svgStyles}>
        <Pills {...colors} viewBox={pillsViewBox} />
      </span>

      {title && (
        <div
          className={[
            classes.pageTitleContainer,
            classes.onTop,
            multiline ? classes.multilinePageTitleContainer : ""
          ].join(" ")}
          style={{
            backgroundColor:
              (smallUp && !multiline) || mediumUp ? colors.overlay : null
          }}
        >
          {typeof icon === "string" ? (
            <OSSIcon
              name={icon}
              {...iconProps}
              className={classes.pageHeaderIcon}
            />
          ) : (
            icon
          )}
          <Typography variant="h6" className={classes.pageHeaderTitle}>
            {typeof title === "string" && title?.includes("OS") ? (
              <>
                <b>OS</b>
                {title?.replace("OS", "")}
              </>
            ) : (
              title
            )}
          </Typography>
        </div>
      )}
      {(hasSearch || buttonAction) && (
        <div
          className={[classes.searchAndButtonWrapper, classes.onTop].join(" ")}
        >
          {hasSearch && (
            <OSSSearchBar
              query={query}
              setQuery={setQuery}
              defaultSortOrder="-modified"
              margin="dense"
              label={searchLabel}
              style={{
                margin: 0,
                minWidth: "50%"
              }}
            />
          )}
          {buttonAction && buttonLabel && (
            <OSSButton
              variant="outlined"
              color="secondary"
              className={classes.tertiaryButtonStyles}
              data-testid="dialog-button"
              onClick={buttonAction}
            >
              {buttonLabel}
            </OSSButton>
          )}
        </div>
      )}
      {/* We want to leave Children here as there are situations the page will want to inject its own components (Profiles HUD) */}
      {children}
    </Grid>
  );
};

export default OSSCatalogHeader;
